import type { LocaleMessages } from "~/locale/locale.d";
import enUS from "~/locale/en-US";

export type SupportedLocale = "en-US";

const locales: Record<SupportedLocale, LocaleMessages> = {
  "en-US": enUS,
};

/**
 * Resolves a dot-separated key path against the locale message tree.
 *
 * @param key - Dot-separated path into LocaleMessages, e.g. `"skillsPage.tiers.expert"`
 * @param locale - BCP 47 locale tag (default `"en-US"`)
 * @returns The resolved leaf value — a string or a message function.
 *
 * @example
 * const t = useI18n();
 * t("skillsPage.tiers.expert")            // → "Expert"
 * t("skillsPage.projectsCount")(3)        // → "3 projects"
 * t("skillsPage.tiers.expert", "en-US")   // explicit locale
 */
export function useI18n(locale: SupportedLocale = "en-US") {
  const messages = locales[locale];

  function t<K extends DeepKeyOf<LocaleMessages>>(
    key: K
  ): DeepValue<LocaleMessages, K> {
    const parts = (key as string).split(".");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let current: any = messages;
    for (const part of parts) {
      current = current[part];
    }
    return current;
  }

  return t;
}

// ---------------------------------------------------------------------------
// Type utilities: deep dot-path keys and their leaf value types
// ---------------------------------------------------------------------------

type Primitive = string | number | boolean | null | undefined;

/** All dot-separated paths that lead to a leaf (string, function, or primitive). */
export type DeepKeyOf<T, Prefix extends string = ""> = {
  [K in keyof T & string]: T[K] extends
    | Primitive
    | ((...args: never[]) => unknown)
    ? Prefix extends ""
      ? K
      : `${Prefix}.${K}`
    : T[K] extends object
      ?
          | (Prefix extends "" ? K : `${Prefix}.${K}`)
          | DeepKeyOf<T[K], Prefix extends "" ? K : `${Prefix}.${K}`>
      : never;
}[keyof T & string];

/** Resolves the value type for a dot-separated path K within T. */
export type DeepValue<
  T,
  K extends string,
> = K extends `${infer Head}.${infer Tail}`
  ? Head extends keyof T
    ? DeepValue<T[Head], Tail>
    : never
  : K extends keyof T
    ? T[K]
    : never;
